import { WarningOutlined } from '@mui/icons-material'
import { Box, CircularProgress, Typography, useTheme } from '@mui/material'
import React, { Fragment } from 'react'
import { colors } from '../../constants/colors'
import { locationState } from '../../context/location-context'
import { HBox, VBox } from '../../elements/basic-elements'
import { ErrorText } from '../../elements/form-elements'
import { BallotReadyRepresentativesObject } from '../../types/ballot-ready-types'
import AddressInput from '../address-input'
import { RepCardData } from './rep-card'
import RepresentativeButton from './representative-button'

const Representatives = ({
  outreach,
  onSelectRep
}: {
  outreach: Queries.SanityOutreach
  onSelectRep: (data: RepCardData) => void
}) => {
  const {
    locationLoading,
    settings,
    representativesData,
    representativesError,
    representativesLoading
  } = locationState()
  const { address } = settings

  const { targets, locationText, targetsOther } = outreach
  const targetsAreOnlyOther = targets.every(target => target === 'other')

  const representatives = targets
    ?.map(representative => {
      const repOfType =
        representative &&
        representativesData &&
        representativesData[representative as keyof BallotReadyRepresentativesObject]
      return repOfType
    })
    .filter(repOfType => repOfType !== undefined) as BallotReadyRepresentativesObject[]

  const fewerRepsThanExpected =
    representativesData &&
    targets &&
    representatives &&
    representatives.length < [...targets].splice(targets.indexOf('other'), 1).length

  const theme = useTheme()
  const background = theme.palette.mode === 'dark' ? colors.DARK_MODE_70 : colors.BLACK_10
  return (
    <VBox gap={2} my={4}>
      <Typography variant="body1" color="textPrimary">
        Be Heard! Contact Your Representatives:
      </Typography>
      {!targetsAreOnlyOther && (
        <Fragment>
          <Box sx={{ pointerEvents: 'all' }}>
            <AddressInput background={background} />
            {(!address || fewerRepsThanExpected) && (
              <Typography variant="body2" color="textSecondary" mt={0.5}>
                <HBox>
                  <Box flex={0} mr={0.5}>
                    *
                  </Box>
                  <Box>
                    {!address && 'Add your location to find your local representatives. '}
                    {locationText ||
                      'Please include your city, state and zipcode for the most accurate results.'}
                  </Box>
                </HBox>
              </Typography>
            )}
          </Box>

          {!fewerRepsThanExpected && targets && targets.length > 0 && (
            <Fragment>
              <Box>
                {representativesLoading || locationLoading ? (
                  <CircularProgress color="secondary" />
                ) : (
                  <VBox gap={1}>
                    {representativesError ? (
                      <ErrorText>{representativesError}</ErrorText>
                    ) : (
                      representatives.map(representative => {
                        const { office, representatives } = representative
                        return (
                          <VBox gap={1}>
                            {representatives?.map(rep => {
                              const party = rep.parties[0]
                              return (
                                <RepresentativeButton
                                  name={`${rep.person.fullName}`}
                                  title={`${party ? party?.name + ', ' : ''}${office}`}
                                  photo={rep.person.headshot.defaultUrl}
                                  onClick={() => {
                                    onSelectRep({
                                      name: rep.person.fullName,
                                      party: party?.name,
                                      photo: rep.person.headshot.defaultUrl,
                                      websites: rep.urls,
                                      title: office,
                                      phones: [
                                        rep.centralPhone
                                          ? { type: 'Primary Phone', phone: rep.centralPhone }
                                          : undefined,
                                        ...rep.person.contacts.map(({ phone, type }) => ({
                                          type,
                                          phone
                                        }))
                                      ]
                                        .filter(phone => !!phone)
                                        .reduce((phones, phone) => {
                                          if (
                                            !phones.find(existing => existing.phone !== phone.phone)
                                          ) {
                                            phones.push(phone)
                                          }
                                          return phones
                                        }, []),

                                      emails: [
                                        rep.primaryEmail
                                          ? { type: 'Primary Email', email: rep.primaryEmail }
                                          : undefined,
                                        ...rep.person.contacts.map(({ email, type }) => ({
                                          type,
                                          email
                                        }))
                                      ]
                                        .filter(email => !!email)
                                        .reduce((emails, email) => {
                                          if (
                                            !emails.find(existing => existing.email !== email.email)
                                          ) {
                                            emails.push(email)
                                          }
                                          return emails
                                        }, [])
                                    })
                                  }}
                                />
                              )
                            })}
                          </VBox>
                        )
                      })
                    )}
                  </VBox>
                )}
              </Box>
            </Fragment>
          )}
          {fewerRepsThanExpected && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                p: 1,
                borderRadius: '9px',
                background: theme => theme.palette.error.light
              }}
            >
              <WarningOutlined />
              <Typography color="textPrimary">
                We didn't find all the representatives for your area that we were hoping for. This
                can happen if your address isn't detailed enough. Consider filling out your address
                again so we can find all the people we want you to reach out to.
              </Typography>
            </Box>
          )}
        </Fragment>
      )}
      {targetsOther && targetsOther.length > 0 && (
        <Box>
          {!targetsAreOnlyOther && (
            <Box my={1}>
              <Typography variant="body1" color="textPrimary">
                Other folks you can contact:
              </Typography>
            </Box>
          )}
          <VBox gap={1}>
            {targetsOther.map(otherTarget => {
              // @ts-expect-error: existing type issue
              const { name, title, photoUrl } = otherTarget

              return (
                <RepresentativeButton
                  name={name}
                  title={title}
                  photo={photoUrl}
                  onClick={() => {
                    onSelectRep(otherTarget)
                  }}
                />
              )
            })}
          </VBox>
        </Box>
      )}
    </VBox>
  )
}

export default Representatives
